import React, { useState, useEffect } from "react";
import { fetchLessonById } from "../../services/lessonService/lessonService";
import { useFileProgressPolling } from "../../hooks/useFileProgressPolling";
import editIcon from "../../assets/icons/editIcon.svg";
import { LessonDetailsProps } from "./LessonDetails.types";
import {
  ActionIconsWrapper,
  LessonDetailsContainer,
  ButtonWrapper,
  EditableInput,
  VideoPlayersContainer,
} from "./LessonDetails.styles";
import Button from "../Buttons/Button";
import AssignmentsContainer from "../AssignmentsContainer";
import IconButton from "../Buttons/IconButton";
import AutoResizableTextarea from "../AutoResizableTextarea/AutoResizableTextarea";
import { FileDisplay } from "../FileToDisplay/FileToDisplay.types";
import DragAndDropField from "../DragAndDropField/DragAndDropField";
import VideoPlayer from "../VideoPlayer/VideoPlayer";
import { pxToRem } from "../../styles/utils";


const LessonDetails: React.FC<LessonDetailsProps> = ({
  title,
  order,
  content,
  category,
  video_url,
  files_from_storage,
  assignments,
  courseId,
  lessonId,
  onSubmit,
  onAssignmentsUpdate,
  isSubscriptionActive
}) => {
  const [isContentEditable, setIsContentEditable] = useState<boolean>(false);
  const [isCategoryEditable, setIsCategoryEditable] = useState<boolean>(false);
  const [isVideoEditable, setIsVideoEditable] = useState<boolean>(false);
  const [AssignmentsToDelete, setAssignmentsToDelete] = useState<number | null>(null);

  const [contentValue, setContentValue] = useState<string>(content);
  const [categoryValue, setCategoryValue] = useState<string>(category || "");
  const [videoURLValue, setVideoURLValue] = useState<string>(video_url || "");
  const [storageFiles, setStorageFiles] = useState<FileDisplay[]>(files_from_storage || []);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  
  // Refs для всех видео элементов в контейнере
  const videoRefs = React.useRef<Map<number, HTMLVideoElement | null>>(new Map());
  
  // Обработчик начала воспроизведения - ставит остальные видео на паузу
  const handleVideoPlay = (playingVideoId: number) => {
    videoRefs.current.forEach((videoElement, videoId) => {
      if (videoId !== playingVideoId && videoElement && !videoElement.paused) {
        videoElement.pause();
      }
    });
  };


  useEffect(() => {
    if (video_url) {
      setVideoURLValue(video_url);
    }
  }, [video_url]);

  // Автообновление прогресса файлов (компонент всегда активен когда смонтирован)
  useFileProgressPolling({
    isActive: true, // LessonDetails активен пока смонтирован
    entityId: lessonId,
    filesFromStorage: storageFiles,
    updateFilesCallback: (files) => {
      // Обновляем состояние, создавая новый массив и новые объекты для триггера реакта
      // Это гарантирует, что React увидит изменения в progress_percent
      setStorageFiles(files.map(file => ({ ...file })));
    },
    fetchFunction: fetchLessonById,
    fetchParams: [courseId, lessonId],
  });

  // Обновляем storageFiles из props только при монтировании или при реальном изменении
  // (не перезаписываем обновления из хука polling)
  useEffect(() => {
    if (files_from_storage && files_from_storage.length > 0) {
      // Проверяем, действительно ли props отличаются от текущего состояния
      // Сравниваем по ID файлов, чтобы не перезаписывать обновления прогресса
      const currentIds = storageFiles.map(f => f.id).sort().join(',');
      const propsIds = files_from_storage.map(f => f.id).sort().join(',');
      
      // Обновляем только если изменился набор файлов (добавились/удалились)
      // или если это первый рендер (storageFiles пустой)
      if (currentIds !== propsIds || storageFiles.length === 0) {
        setStorageFiles(files_from_storage);
      }
    } else if (!files_from_storage || files_from_storage.length === 0) {
      // Если props пустые, сбрасываем состояние только если текущее тоже не пустое
      if (storageFiles.length > 0) {
        setStorageFiles([]);
      }
    }
  }, [files_from_storage]); // Оставляем только files_from_storage в зависимостях, но сравниваем содержимое

  const handleSave = async () => {
    setIsSaving(true);
    
    try {
      const dataToSubmit: Record<string, string | File[] | FileDisplay[] | null> = {
        title,
        order: String(order),
        description: contentValue,
        category: categoryValue,
        video_url: videoURLValue,
        files_from_storage: storageFiles, 
        files: newFiles, 
      };

      await onSubmit(dataToSubmit, { isEditing: true, lessonId: lessonId });
    } catch (error) {
      console.error('Ошибка при сохранении урока:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleFileSelected = (files: File[] | null) => {
    setNewFiles(files || []);
  };
  const handleRemoveFile = (fileId: number) => {
    setStorageFiles((prev) => prev.filter((file) => file.id !== fileId));
  };

  return (
    <LessonDetailsContainer>
      {/* Описание */}
      <ActionIconsWrapper isEditing={isContentEditable}>
        <AutoResizableTextarea
          value={contentValue}
          disabled={!isContentEditable}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            setContentValue(e.target.value)
          }
        />
        <div>
          <IconButton
            icon={editIcon}
            altText="Редактировать"
            onClick={() => setIsContentEditable((prev) => !prev)}
          />
        </div>
      </ActionIconsWrapper>

      {/* Категория */}
      <ActionIconsWrapper isEditing={isCategoryEditable}>
        <EditableInput
          type="text"
          value={categoryValue}
          disabled={!isCategoryEditable}
          onChange={(e) => setCategoryValue(e.target.value)}
        />
        <div>
          <IconButton
            icon={editIcon}
            altText="Редактировать"
            onClick={() => setIsCategoryEditable((prev) => !prev)}
          />
        </div>
      </ActionIconsWrapper>

      <ActionIconsWrapper isEditing={isVideoEditable}>
        {isVideoEditable ? (
          <EditableInput
            type="text"
            value={videoURLValue}
            disabled={!isVideoEditable}
            onChange={(e) => setVideoURLValue(e.target.value)}
          />
        ) : (
          <a
            href={videoURLValue || "#"}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "rgba(114, 82, 220, 1)",
              fontWeight: 500,
              fontSize: "18px",
              textDecoration: "none", 
              cursor: videoURLValue ? "pointer" : "default",
            }}
            onClick={(e) => {
              if (!videoURLValue) e.preventDefault(); 
            }}
          >
            {videoURLValue ? "Ссылка на материал" : "Добавьте ссылку на материал"}
          </a>
        )}
        {isSubscriptionActive && (
          <div>
            <IconButton
              icon={editIcon}
              altText="Редактировать"
              onClick={() => setIsVideoEditable((prev) => !prev)} 
            />
          </div>
        )}
      </ActionIconsWrapper>

      <DragAndDropField
        files={newFiles}
        files_from_storage={storageFiles}
        onFileSelected={handleFileSelected}
        onRemoveFile={handleRemoveFile}
        placeholder="Перетащите файлы сюда или нажмите, чтобы выбрать."
        maxFiles={5}
        disabled={!isSubscriptionActive}
      />

      {/* Видео плееры для готовых видео */}
      {(() => {
        const readyVideos = storageFiles.filter(
          (file) => file.type === "video" && file.status === "ready"
        );
        console.log("LessonDetails: Готовые видео для воспроизведения:", readyVideos);
        if (readyVideos.length === 0) return null;
        
        return (
          <VideoPlayersContainer>
            {readyVideos.map((file) => (
              <VideoPlayer
                key={file.id}
                lessonId={lessonId}
                videoId={file.id}
                courseId={courseId}
                videoName={file.name}
                onVideoRef={(ref) => {
                  if (ref) {
                    videoRefs.current.set(file.id, ref);
                  } else {
                    videoRefs.current.delete(file.id);
                  }
                }}
                onPlay={() => handleVideoPlay(file.id)}
              />
            ))}
          </VideoPlayersContainer>
        );
      })()}

      {/* Домашнее задание */}
      <div>
        <AssignmentsContainer
        courseId={courseId}
        lessonId={lessonId}
        isSubscriptionActive={isSubscriptionActive}
        />
        {isSubscriptionActive && (
          <ButtonWrapper>
            <Button
              onClick={handleSave} // Сохраняем урок
              variant="active"
              width= {`${pxToRem(160)}`}
              height={`${pxToRem(50)}`}
              disabled={isSaving}
            >
              {isSaving ? "Сохранение..." : "Сохранить"}
            </Button>
          </ButtonWrapper>
        )}
      </div>
    </LessonDetailsContainer>
  );
};

export default LessonDetails;


